import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, Check, X } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { fmtDate } from "../lib/format";

const eur = (n) => "€ " + Number(n || 0).toFixed(2).replace(".", ",");
const STATUS = { pending: ["In attesa", "bg-amber-50 text-amber-700 border-amber-200"], confirmed: ["Confermato", "bg-emerald-50 text-emerald-700 border-emerald-200"], rejected: ["Rifiutato", "bg-red-50 text-red-600 border-red-200"] };

export default function TransfersPanel({ onChanged }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/super-admin/transfers").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const act = async (t, action) => {
    const msg = action === "confirm" ? `Confermi l'accredito di ${eur(t.amount)} da ${t.email}? Il piano verrà attivato subito.` : `Rifiutare il bonifico di ${t.email}?`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/super-admin/transfers/${t.id}/${action}`);
      toast.success(action === "confirm" ? "Accredito confermato: piano attivato" : "Bonifico rifiutato");
      load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };

  const pending = rows.filter((r) => r.status === "pending").length;
  return (
    <div data-testid="transfers-panel" className="bg-card border border-slate-200/80 rounded-xl shadow-sm p-6 mt-6">
      <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
        <Landmark style={{ width: 18, height: 18 }} className="text-primary" /> Bonifici abbonamenti
        {pending > 0 && <Badge data-testid="transfers-pending-count" className="bg-amber-500">{pending} da verificare</Badge>}
      </h2>
      <p className="text-xs text-slate-500 mt-1">Conferma l'accredito solo dopo averlo visto sul conto: il piano si attiva in quel momento.</p>
      {rows.length === 0 ? (
        <p data-testid="transfers-empty" className="text-sm text-slate-400 mt-4">Nessun bonifico registrato.</p>
      ) : (
        <div className="mt-4 divide-y divide-slate-100">
          {rows.map((t) => (
            <div key={t.id} data-testid={`transfer-row-${t.reference}`} className="py-3 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <p className="text-sm font-medium text-slate-900 break-all">{t.email} · <span className="font-mono">{eur(t.amount)}</span></p>
                <p className="text-xs text-slate-500 break-words">{t.causale} · {fmtDate(t.created_at)}</p>
              </div>
              <Badge variant="outline" className={STATUS[t.status]?.[1]}>{STATUS[t.status]?.[0]}</Badge>
              {t.status === "pending" && (
                <div className="flex gap-2">
                  <Button size="sm" data-testid={`transfer-confirm-${t.reference}`} onClick={() => act(t, "confirm")} className="bg-emerald-600 hover:bg-emerald-700 font-semibold"><Check style={{ width: 14, height: 14 }} className="mr-1" />Conferma accredito</Button>
                  <Button size="sm" variant="outline" data-testid={`transfer-reject-${t.reference}`} onClick={() => act(t, "reject")}><X style={{ width: 14, height: 14 }} /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
